import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListSuppliers,
  useUpsertSupplierChannelManagerOverride,
  useDeleteSupplierChannelManagerOverride,
  useCreateSupplier,
  useUpdateSupplier,
  useDeleteSupplier,
  useMergeSupplier,
  useCheckSupplierDuplicates,
  useBulkImportSalesforceSuppliers,
  useListSalesforceAccountsForImport,
  useRequestSupplierSfAdd,
  useDismissSupplierReview,
  getListSuppliersQueryKey,
  TdmSupplier,
  TdmSupplierContact,
  ChannelManagerOverride,
  MatchCandidate,
  SfAccountForImport,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AddressInput, AddressValue, emptyAddress, addressFromApi, addressToPayload, formatAddressDisplay } from "@/components/AddressInput";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  Loader2,
  Mail,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  Users,
  X,
  AlertTriangle,
  GitMerge,
  ExternalLink,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import { getErrorMessage } from "@/lib/error";
import { toastError } from "@/lib/toast-error";
import { useGetMailbox, useGetMe } from "@workspace/api-client-react";

const PAGE_SIZE = 50;

export default function SuppliersPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debounced = useDebounce(search, 250);
  const queryClient = useQueryClient();
  const { data: mailbox } = useGetMailbox();
  const { data: me } = useGetMe();
  const isAdmin = !!me?.isAdmin;

  // Reset to first page whenever the search query changes
  const prevDebounced = useRef(debounced);
  if (prevDebounced.current !== debounced) {
    prevDebounced.current = debounced;
    if (page !== 1) setPage(1);
  }

  const { data, isLoading, isError, error } = useListSuppliers(
    { q: debounced || undefined, page, pageSize: PAGE_SIZE },
    {
      query: {
        queryKey: [...getListSuppliersQueryKey(), debounced, page],
        retry: (failureCount: number, err: unknown) => {
          const status = (err as { status?: number } | null)?.status;
          if (typeof status === "number" && status >= 400 && status < 600) return false;
          return failureCount < 3;
        },
      },
    },
  );

  const adminConfigured = data?.adminEmailConfigured ?? false;
  const mailboxConnected = mailbox?.connected ?? false;
  const suppliers = data?.results ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const needsReviewSuppliers = suppliers.filter((s) => !!s.needsReview);

  const [editingOverride, setEditingOverride] = useState<TdmSupplier | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<TdmSupplier | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getListSuppliersQueryKey() });
  }, [queryClient]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Suppliers</h1>
          <p className="text-muted-foreground">
            Your curated supplier directory. Add suppliers manually or import from Salesforce.
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowImportDialog(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Import from Salesforce
            </Button>
            <Button size="sm" onClick={() => { setEditingSupplier(null); setShowAddDialog(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add supplier
            </Button>
          </div>
        )}
      </div>

      {!adminConfigured && (
        <div className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-amber-800 dark:text-amber-400">
              Admin email not configured
            </h4>
            <p className="text-sm text-amber-700/80 dark:text-amber-500/80 mt-1">
              Set the <code className="font-mono">SF_ADMIN_EMAIL</code> environment variable so
              channel-manager corrections and Salesforce add requests can be emailed to the admin.
            </p>
          </div>
        </div>
      )}

      {!mailboxConnected && (
        <div className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-amber-800 dark:text-amber-400">Mailbox not connected</h4>
            <p className="text-sm text-amber-700/80 dark:text-amber-500/80 mt-1">
              Connect your Gmail or Microsoft 365 mailbox in{" "}
              <a href="/settings" className="underline">Settings</a> to send admin emails.
            </p>
          </div>
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search suppliers or channel managers..."
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : isError ? (
        <div className="p-4 border border-destructive/40 bg-destructive/10 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-destructive">Couldn't load suppliers</h4>
            <p className="text-sm text-destructive/90 mt-1">
              {getErrorMessage(error, "Failed to load suppliers")}
            </p>
          </div>
        </div>
      ) : suppliers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center p-12 text-center text-muted-foreground">
            <Building2 className="h-10 w-10 mb-4 opacity-20" />
            <p className="font-medium text-foreground">No suppliers yet</p>
            <p className="text-sm mt-1 max-w-sm">
              {isAdmin
                ? 'Add suppliers manually or import them from Salesforce using the buttons above.'
                : 'No suppliers have been added yet. Contact an admin to set up the supplier directory.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {needsReviewSuppliers.length > 0 && (
            <NeedsReviewBanner
              count={needsReviewSuppliers.length}
              suppliers={needsReviewSuppliers}
              onResolved={refresh}
              isAdmin={isAdmin}
            />
          )}
          <div className="space-y-3">
            {suppliers.map((s) => (
              <SupplierRow
                key={s.id}
                supplier={s}
                isAdmin={isAdmin}
                onEdit={() => { setEditingSupplier(s); setShowAddDialog(true); }}
                onEditOverride={() => setEditingOverride(s)}
                onCleared={refresh}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-muted-foreground">
                {total} supplier{total !== 1 ? "s" : ""} — page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {showAddDialog && (
        <AddSupplierDialog
          open={showAddDialog}
          existing={editingSupplier}
          onClose={() => { setShowAddDialog(false); setEditingSupplier(null); }}
          onSaved={() => { setShowAddDialog(false); setEditingSupplier(null); refresh(); }}
          adminConfigured={adminConfigured}
          mailboxConnected={mailboxConnected}
        />
      )}

      <OverrideDialog
        supplier={editingOverride}
        open={!!editingOverride}
        onClose={() => setEditingOverride(null)}
        onSaved={() => { setEditingOverride(null); refresh(); }}
        adminConfigured={adminConfigured}
        mailboxConnected={mailboxConnected}
      />

      {showImportDialog && (
        <ImportFromSalesforceDialog
          open={showImportDialog}
          onClose={() => setShowImportDialog(false)}
          onImported={() => { setShowImportDialog(false); refresh(); }}
        />
      )}
    </div>
  );
}

function SupplierRow({
  supplier,
  isAdmin,
  onEdit,
  onEditOverride,
  onCleared,
}: {
  supplier: TdmSupplier;
  isAdmin: boolean;
  onEdit: () => void;
  onEditOverride: () => void;
  onCleared: () => void;
}) {
  const del = useDeleteSupplierChannelManagerOverride();
  const deleteSupplier = useDeleteSupplier();
  const cms = supplier.channelManagers ?? [];
  const sfCms = supplier.sfChannelManagers ?? [];
  const override = supplier.override;
  const used = supplier.usedInDeal;

  const handleClear = () => {
    if (
      !window.confirm(
        `Clear the override for ${supplier.name}? Future emails will go back to the local channel manager(s).`,
      )
    )
      return;
    del.mutate(
      { sfAccountId: supplier.sfAccountId! },
      {
        onSuccess: () => { toast.success("Override cleared"); onCleared(); },
        onError: (err: unknown) => toastError(err, "Failed to clear override"),
      },
    );
  };

  const handleDelete = () => {
    if (!window.confirm(`Delete supplier "${supplier.name}"? This cannot be undone.`)) return;
    deleteSupplier.mutate(
      { id: supplier.id },
      {
        onSuccess: () => { toast.success("Supplier deleted"); onCleared(); },
        onError: (err: unknown) => toastError(err, "Failed to delete supplier"),
      },
    );
  };

  type Action = {
    key: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    destructive?: boolean;
  };
  const actions: Action[] = [];
  if (isAdmin) {
    actions.push({
      key: "edit",
      label: "Edit",
      icon: <Pencil className="h-3 w-3 mr-2" />,
      onClick: onEdit,
    });
  }
  if (supplier.sfAccountId) {
    actions.push({
      key: "override",
      label: override ? "Edit override" : "Submit correction",
      icon: <Pencil className="h-3 w-3 mr-2" />,
      onClick: onEditOverride,
    });
  }
  if (override && supplier.sfAccountId && isAdmin) {
    actions.push({
      key: "clear",
      label: "Clear override",
      icon: del.isPending ? (
        <Loader2 className="h-3 w-3 mr-2 animate-spin" />
      ) : (
        <Trash2 className="h-3 w-3 mr-2" />
      ),
      onClick: handleClear,
      disabled: del.isPending,
      destructive: true,
    });
  }
  if (isAdmin) {
    actions.push({
      key: "delete",
      label: "Delete",
      icon: deleteSupplier.isPending ? (
        <Loader2 className="h-3 w-3 mr-2 animate-spin" />
      ) : (
        <Trash2 className="h-3 w-3 mr-2" />
      ),
      onClick: handleDelete,
      disabled: deleteSupplier.isPending,
      destructive: true,
    });
  }

  return (
    <Card className={used ? "border-primary/40 bg-primary/5" : ""}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 flex-wrap">
              <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1.5" />
              <h3 className="font-semibold text-lg break-words min-w-0 flex-1">{supplier.name}</h3>
              {actions.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="md:hidden h-9 w-9 -mr-2 -mt-1 flex-shrink-0"
                      aria-label="Supplier actions"
                    >
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {actions.map((a) => (
                      <DropdownMenuItem
                        key={a.key}
                        onSelect={(e) => {
                          e.preventDefault();
                          if (!a.disabled) a.onClick();
                        }}
                        disabled={a.disabled}
                        className={a.destructive ? "text-destructive focus:text-destructive" : ""}
                      >
                        {a.icon}
                        {a.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Badge
                variant="outline"
                className={`text-xs ${supplier.source === "salesforce" ? "border-blue-400 text-blue-700 dark:text-blue-400" : "border-purple-400 text-purple-700 dark:text-purple-400"}`}
              >
                {supplier.source === "salesforce" ? "Salesforce" : "Manual"}
              </Badge>
              {used && (
                <Badge variant="default" className="text-xs">
                  Used in {supplier.registrationCount ?? 0} registration
                  {(supplier.registrationCount ?? 0) === 1 ? "" : "s"}
                </Badge>
              )}
              {override && (
                <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 dark:text-amber-400">
                  Override active
                </Badge>
              )}
              {supplier.needsReview && (
                <Badge variant="outline" className="text-xs border-orange-400 text-orange-700 dark:text-orange-400">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Needs review
                </Badge>
              )}
            </div>

            {supplier.sfAccountId && (
              <div className="text-xs text-muted-foreground font-mono mt-1 break-all">
                SF: {supplier.sfAccountId}
              </div>
            )}
            {supplier.autoLinked && (
              <div className="text-xs text-muted-foreground mt-1 flex items-start gap-1">
                <GitMerge className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <span>
                  Auto-linked from Salesforce on{" "}
                  {new Date(supplier.autoLinked.linkedAt).toLocaleDateString()} —{" "}
                  {supplier.autoLinked.signals.length > 0
                    ? supplier.autoLinked.signals.join("; ")
                    : `match score ${Math.round(supplier.autoLinked.score * 100)}%`}
                </span>
              </div>
            )}
            {supplier.website && (
              <div className="text-xs text-muted-foreground mt-0.5 flex items-start gap-1 min-w-0">
                <ExternalLink className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <a href={supplier.website.startsWith("http") ? supplier.website : `https://${supplier.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline break-all min-w-0">
                  {supplier.website}
                </a>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                  <Users className="h-3 w-3" /> Channel manager(s)
                </div>
                {cms.length === 0 && sfCms.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">None associated</div>
                ) : (
                  <ul className="space-y-1">
                    {cms.map((cm) => (
                      <li key={cm.id} className="text-sm">
                        <div className="font-medium">
                          {cm.name}
                          {cm.isPrimary && (
                            <span className="ml-1 text-xs text-muted-foreground">(primary)</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-start gap-1 min-w-0">
                          <Mail className="h-3 w-3 mt-0.5 flex-shrink-0" /> <span className="break-all min-w-0">{cm.email}</span>
                        </div>
                        {cm.title && <div className="text-xs text-muted-foreground break-words">{cm.title}</div>}
                      </li>
                    ))}
                    {cms.length === 0 && sfCms.map((cm) => (
                      <li key={cm.id} className="text-sm">
                        <div className="font-medium break-words">{cm.name}</div>
                        <div className="text-xs text-muted-foreground flex items-start gap-1 min-w-0">
                          <Mail className="h-3 w-3 mt-0.5 flex-shrink-0" /> <span className="break-all min-w-0">{cm.email}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {supplier.sfAccountId && (
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Local override
                  </div>
                  {override ? (
                    <div className="text-sm">
                      <div className="font-medium break-words">{override.name}</div>
                      <div className="text-xs text-muted-foreground flex items-start gap-1 min-w-0">
                        <Mail className="h-3 w-3 mt-0.5 flex-shrink-0" /> <span className="break-all min-w-0">{override.email}</span>
                      </div>
                      {override.title && <div className="text-xs text-muted-foreground break-words">{override.title}</div>}
                      {override.submittedByEmail && (
                        <div className="text-xs text-muted-foreground mt-1 break-all">
                          Submitted by {override.submittedByEmail}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground italic">No local override</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:grid md:grid-cols-1 gap-2 md:w-44 flex-shrink-0">
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Pencil className="h-3 w-3 mr-2" />
                Edit
              </Button>
            )}
            {supplier.sfAccountId && (
              <Button variant="outline" size="sm" onClick={onEditOverride}>
                <Pencil className="h-3 w-3 mr-2" />
                {override ? "Edit override" : "Submit correction"}
              </Button>
            )}
            {override && supplier.sfAccountId && isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                disabled={del.isPending}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                {del.isPending ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Trash2 className="h-3 w-3 mr-2" />}
                Clear override
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                disabled={deleteSupplier.isPending}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                {deleteSupplier.isPending ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Trash2 className="h-3 w-3 mr-2" />}
                Delete
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type CMInput = { name: string; email: string; phone: string; title: string; isPrimary: boolean };

function AddSupplierDialog({
  open,
  existing,
  onClose,
  onSaved,
  adminConfigured,
  mailboxConnected,
}: {
  open: boolean;
  existing: TdmSupplier | null;
  onClose: () => void;
  onSaved: () => void;
  adminConfigured: boolean;
  mailboxConnected: boolean;
}) {
  const isEditing = !!existing;
  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier();
  const checkDuplicates = useCheckSupplierDuplicates();
  const mergeSupplier = useMergeSupplier();

  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [address, setAddress] = useState<AddressValue>(emptyAddress());
  const [notes, setNotes] = useState("");
  const [requestSfAdd, setRequestSfAdd] = useState(false);
  const [cms, setCms] = useState<CMInput[]>([{ name: "", email: "", phone: "", title: "", isPrimary: true }]);
  const [step, setStep] = useState<"form" | "duplicates">("form");
  const [candidates, setCandidates] = useState<MatchCandidate[]>([]);

  useEffect(() => {
    if (open) {
      if (existing) {
        setName(existing.name);
        setWebsite(existing.website ?? "");
        setAddress(addressFromApi(existing.address));
        setNotes(existing.notes ?? "");
        setRequestSfAdd(false);
        setCms(
          existing.channelManagers?.length
            ? existing.channelManagers.map((cm) => ({
                name: cm.name,
                email: cm.email,
                phone: cm.phone ?? "",
                title: cm.title ?? "",
                isPrimary: cm.isPrimary ?? false,
              }))
            : [{ name: "", email: "", phone: "", title: "", isPrimary: true }],
        );
      } else {
        setName("");
        setWebsite("");
        setAddress(emptyAddress());
        setNotes("");
        setRequestSfAdd(false);
        setCms([{ name: "", email: "", phone: "", title: "", isPrimary: true }]);
      }
      setStep("form");
      setCandidates([]);
    }
  }, [open, existing]);

  const addCmRow = () =>
    setCms((prev) => [...prev, { name: "", email: "", phone: "", title: "", isPrimary: false }]);

  const removeCmRow = (i: number) =>
    setCms((prev) => prev.filter((_, idx) => idx !== i));

  const updateCm = (i: number, field: keyof CMInput, value: string | boolean) =>
    setCms((prev) => prev.map((cm, idx) => (idx === i ? { ...cm, [field]: value } : cm)));

  const validCms = cms.filter((cm) => cm.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cm.email.trim()));
  const canSubmit = name.trim().length > 0 && validCms.length >= 1;

  const handleCheckAndSave = async () => {
    if (isEditing) {
      handleSave();
      return;
    }
    // Run duplicate check first — errors block creation (don't silently proceed)
    const contactEmails = validCms.map((cm) => cm.email.trim());
    let result: { candidates: MatchCandidate[] };
    try {
      result = await checkDuplicates.mutateAsync({
        data: {
          name: name.trim(),
          website: website.trim() || undefined,
          contactEmails: contactEmails.length ? contactEmails : undefined,
          excludeId: (existing as TdmSupplier | null)?.id,
        },
      });
    } catch (err) {
      toastError(err, "Duplicate check failed — please try again.");
      return;
    }

    if (result.candidates.length > 0) {
      setCandidates(result.candidates);
      setStep("duplicates");
    } else {
      handleSave();
    }
  };

  const handleSave = async (acknowledgedDuplicates = false) => {
    const cmPayload = validCms.map((cm) => ({
      name: cm.name.trim(),
      email: cm.email.trim(),
      phone: cm.phone.trim() || undefined,
      title: cm.title.trim() || undefined,
      isPrimary: cm.isPrimary,
    }));

    if (isEditing) {
      const updatePayload = {
        name: name.trim(),
        website: website.trim() || undefined,
        address: addressToPayload(address),
        notes: notes.trim() || undefined,
        channelManagers: cmPayload,
        requestSfAdd: requestSfAdd && !existing?.sfAccountId,
      };
      updateSupplier.mutate(
        { id: existing!.id, data: updatePayload },
        {
          onSuccess: () => {
            toast.success(
              requestSfAdd && !existing?.sfAccountId
                ? "Supplier updated and SF add request sent"
                : "Supplier updated",
            );
            onSaved();
          },
          onError: (err) => toastError(err, "Failed to update supplier"),
        },
      );
    } else {
      const createPayload = {
        name: name.trim(),
        website: website.trim() || undefined,
        address: addressToPayload(address),
        notes: notes.trim() || undefined,
        channelManagers: cmPayload,
        requestSfAdd,
        acknowledgedDuplicates,
      };
      createSupplier.mutate(
        { data: createPayload },
        {
          onSuccess: () => {
            toast.success(
              requestSfAdd ? "Supplier added and SF add request sent" : "Supplier added",
            );
            onSaved();
          },
          onError: (err) => toastError(err, "Failed to add supplier"),
        },
      );
    }
  };

  const handleMergeInto = async (targetId: string) => {
    // Duplicate check only runs during create (isEditing returns early before reaching
    // this panel). So here we always create the new supplier first, then merge it into
    // the chosen existing supplier (which keeps its data and absorbs the new CMs).
    try {
      const payload = {
        name: name.trim(),
        website: website.trim() || undefined,
        address: addressToPayload(address),
        notes: notes.trim() || undefined,
        channelManagers: validCms.map((cm) => ({
          name: cm.name.trim(),
          email: cm.email.trim(),
          phone: cm.phone.trim() || undefined,
          title: cm.title.trim() || undefined,
          isPrimary: cm.isPrimary,
        })),
        requestSfAdd: false,
        acknowledgedDuplicates: true,
      };
      const created = await createSupplier.mutateAsync({ data: payload });
      await mergeSupplier.mutateAsync({ id: created.id, data: { targetId } });
      toast.success("Supplier data merged into existing supplier");
      onSaved();
    } catch (err) {
      toastError(err, "Failed to merge suppliers");
    }
  };

  const isPending =
    createSupplier.isPending ||
    updateSupplier.isPending ||
    checkDuplicates.isPending ||
    mergeSupplier.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? `Edit supplier — ${existing!.name}` : "Add supplier"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the supplier details and channel manager contacts."
              : "Manually add a supplier to your TDM directory."}
          </DialogDescription>
        </DialogHeader>

        {step === "duplicates" ? (
          <DuplicateResolutionPanel
            candidates={candidates}
            onUseExisting={(_id) => {
              // "Use existing" intentionally does not create a new supplier.
              // The admin has confirmed the existing supplier is correct,
              // so we close the dialog without any additional association step.
              toast.success("No duplicate created — using the existing supplier.");
              onSaved();
            }}
            onMergeInto={handleMergeInto}
            onConfirmNew={() => { setStep("form"); handleSave(true); }}
            onBack={() => setStep("form")}
            isPending={isPending}
          />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="s-name">Name *</Label>
                <Input
                  id="s-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Acme Corp"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="s-website">Website</Label>
                <Input
                  id="s-website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://acme.com"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Address</Label>
                <AddressInput value={address} onChange={setAddress} idPrefix="s-addr" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="s-notes">Notes</Label>
                <Textarea
                  id="s-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal notes about this supplier..."
                  rows={2}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Channel manager contacts</Label>
                <Button variant="ghost" size="sm" onClick={addCmRow} className="h-7 text-xs">
                  <Plus className="h-3 w-3 mr-1" /> Add contact
                </Button>
              </div>
              <div className="space-y-3">
                {cms.map((cm, i) => (
                  <div key={i} className="p-3 border rounded-lg space-y-2 bg-muted/10">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Contact {i + 1}</span>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                          <Checkbox
                            checked={cm.isPrimary}
                            onCheckedChange={(v) => updateCm(i, "isPrimary", !!v)}
                          />
                          Primary
                        </label>
                        {cms.length > 1 && (
                          <button
                            onClick={() => removeCmRow(i)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="sr-only">Name</Label>
                        <Input
                          placeholder="Name *"
                          value={cm.name}
                          onChange={(e) => updateCm(i, "name", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="sr-only">Email</Label>
                        <Input
                          type="email"
                          placeholder="Email *"
                          value={cm.email}
                          onChange={(e) => updateCm(i, "email", e.target.value)}
                        />
                      </div>
                      <div>
                        <Input
                          placeholder="Phone"
                          value={cm.phone}
                          onChange={(e) => updateCm(i, "phone", e.target.value)}
                        />
                      </div>
                      <div>
                        <Input
                          placeholder="Title"
                          value={cm.title}
                          onChange={(e) => updateCm(i, "title", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {!(existing as TdmSupplier | null)?.sfAccountId && (
              <div className="flex items-start gap-2 p-3 border rounded-lg bg-muted/10">
                <Checkbox
                  id="req-sf-add"
                  checked={requestSfAdd}
                  onCheckedChange={(v) => setRequestSfAdd(!!v)}
                  disabled={!adminConfigured || !mailboxConnected}
                />
                <div>
                  <label
                    htmlFor="req-sf-add"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Request this supplier be added to Salesforce
                  </label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Sends a pre-filled email to the Salesforce admin with supplier details.
                    {(!adminConfigured || !mailboxConnected) && (
                      <span className="text-amber-600 dark:text-amber-400 ml-1">
                        {!adminConfigured ? "Requires SF_ADMIN_EMAIL to be configured." : "Requires a connected mailbox."}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {step === "form" && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={handleCheckAndSave} disabled={!canSubmit || isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Save changes" : "Check & add"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DuplicateResolutionPanel({
  candidates,
  onUseExisting,
  onMergeInto,
  onConfirmNew,
  onBack,
  isPending,
}: {
  candidates: MatchCandidate[];
  onUseExisting: (id: string) => void;
  onMergeInto: (id: string) => void;
  onConfirmNew: () => void;
  onBack: () => void;
  isPending: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-amber-800 dark:text-amber-300 text-sm">
            Possible duplicate{candidates.length > 1 ? "s" : ""} found
          </p>
          <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-1">
            The following existing suppliers may match what you're adding. Choose how to proceed.
          </p>
        </div>
      </div>

      <div className="space-y-3 max-h-64 overflow-y-auto">
        {candidates.map((c) => (
          <div key={c.id} className="border rounded-lg p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-medium text-sm">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {c.signals.join(" · ")}
                </div>
                <div className="text-xs text-muted-foreground">
                  Match score: {Math.round(c.score * 100)}%
                </div>
              </div>
              <Badge
                variant="outline"
                className={`text-xs flex-shrink-0 ${
                  c.score >= 0.9
                    ? "border-red-400 text-red-600 dark:text-red-400"
                    : c.score >= 0.7
                      ? "border-amber-400 text-amber-600 dark:text-amber-400"
                      : "border-gray-400 text-gray-600"
                }`}
              >
                {c.score >= 0.9 ? "High" : c.score >= 0.7 ? "Medium" : "Low"} match
              </Badge>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => onUseExisting(c.id)}>
                Use existing
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7"
                onClick={() => onMergeInto(c.id)}
                disabled={isPending}
              >
                <GitMerge className="h-3 w-3 mr-1" />
                Merge into this
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-2 border-t">
        <Button variant="outline" size="sm" onClick={onBack} disabled={isPending}>
          Back
        </Button>
        <Button size="sm" onClick={onConfirmNew} disabled={isPending} className="flex-1">
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          This is a new supplier — add anyway
        </Button>
      </div>
    </div>
  );
}

function ImportFromSalesforceDialog({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}) {
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 300);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const bulkImport = useBulkImportSalesforceSuppliers();

  const { data, isLoading, isError, error } = useListSalesforceAccountsForImport(
    debounced ? { q: debounced } : undefined,
  );

  const accounts = data?.results ?? [];
  const available = accounts.filter((a) => !a.alreadyImported);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleImport = () => {
    const ids = Array.from(selected);
    bulkImport.mutate(
      { data: { sfAccountIds: ids } },
      {
        onSuccess: (result) => {
          toast.success(
            `Imported ${result.created} new, linked ${result.linked}, skipped ${result.skipped}`,
          );
          onImported();
        },
        onError: (err) => toastError(err, "Failed to import suppliers"),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import from Salesforce</DialogTitle>
          <DialogDescription>
            Select Salesforce partner accounts to import as TDM suppliers. Rows flagged as
            likely duplicates will be linked to the existing supplier rather than creating a new record.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search Salesforce accounts..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-3 text-sm text-destructive border border-destructive/30 rounded-lg">
              {getErrorMessage(error, "Failed to load Salesforce accounts")}
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-2 border rounded-lg p-2">
              {accounts.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No accounts found. Try a different search term.
                </p>
              ) : (
                accounts.map((a) => (
                  <SfAccountRow
                    key={a.id}
                    account={a}
                    selected={selected.has(a.id)}
                    onToggle={() => toggleSelect(a.id)}
                  />
                ))
              )}
            </div>
          )}

          {selected.size > 0 && (
            <div className="text-sm text-muted-foreground">
              {selected.size} account{selected.size !== 1 ? "s" : ""} selected
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={bulkImport.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={selected.size === 0 || bulkImport.isPending}
          >
            {bulkImport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Import {selected.size > 0 ? `${selected.size} supplier${selected.size !== 1 ? "s" : ""}` : "selected"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SfAccountRow({
  account,
  selected,
  onToggle,
}: {
  account: SfAccountForImport;
  selected: boolean;
  onToggle: () => void;
}) {
  const isDuplicate = Boolean(account.duplicateOfId);
  const isImported = account.alreadyImported;

  return (
    <div
      className={`flex items-start gap-3 p-2.5 rounded-md border cursor-pointer transition-colors ${
        isImported
          ? "opacity-50 cursor-not-allowed bg-muted/30"
          : selected
            ? "bg-primary/5 border-primary/30"
            : "hover:bg-muted/30"
      }`}
      onClick={() => !isImported && onToggle()}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={() => !isImported && onToggle()}
        disabled={isImported}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{account.name}</span>
          {isImported && (
            <Badge variant="outline" className="text-xs border-emerald-400 text-emerald-700 dark:text-emerald-400">
              Already imported
            </Badge>
          )}
          {!isImported && isDuplicate && (
            <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 dark:text-amber-400">
              Likely duplicate of {account.duplicateOfName ?? "existing supplier"}
              {account.duplicateScore != null ? ` (${Math.round(account.duplicateScore * 100)}%)` : ""}
            </Badge>
          )}
        </div>
        {account.channelManagers.length > 0 && (
          <div className="text-xs text-muted-foreground mt-0.5">
            CMs: {account.channelManagers.map((cm) => cm.name).join(", ")}
          </div>
        )}
        {isDuplicate && !isImported && (
          <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
            Selecting this will link its SF ID to the existing supplier instead of creating a new record.
          </div>
        )}
      </div>
    </div>
  );
}

function OverrideDialog({
  supplier,
  open,
  onClose,
  onSaved,
  adminConfigured,
  mailboxConnected,
}: {
  supplier: TdmSupplier | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  adminConfigured: boolean;
  mailboxConnected: boolean;
}) {
  const upsert = useUpsertSupplierChannelManagerOverride();
  const initial = useMemo<ChannelManagerOverride | undefined>(
    () => supplier?.override ?? undefined,
    [supplier],
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setEmail(initial?.email ?? "");
      setPhone(initial?.phone ?? "");
      setTitle(initial?.title ?? "");
      setNotes(initial?.notes ?? "");
    }
  }, [open, initial]);

  if (!supplier || !supplier.sfAccountId) return null;

  const canSubmit =
    name.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    adminConfigured &&
    mailboxConnected;

  const handleSubmit = () => {
    upsert.mutate(
      {
        sfAccountId: supplier.sfAccountId!,
        data: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          title: title.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success("Override saved and admin notification email sent.");
          onSaved();
        },
        onError: (err: unknown) =>
          toastError(err, "Failed to save override"),
      },
    );
  };

  const sfCms = supplier.sfChannelManagers ?? [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Correct channel manager — {supplier.name}</DialogTitle>
          <DialogDescription>
            Save the correct channel manager locally and email the TDM Salesforce admin so they
            can update it upstream. Future deals to this supplier will use the override.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {sfCms.length > 0 && (
            <div className="p-3 rounded-md border bg-muted/30 text-sm">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                Currently in Salesforce
              </div>
              {sfCms.map((cm) => (
                <div key={cm.id}>
                  {cm.name} &lt;{cm.email}&gt;
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-1">
              <Label htmlFor="cm-name">Correct name *</Label>
              <Input
                id="cm-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-1">
              <Label htmlFor="cm-email">Correct email *</Label>
              <Input
                id="cm-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@supplier.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cm-title">Title</Label>
              <Input
                id="cm-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Channel Manager"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cm-phone">Phone</Label>
              <Input
                id="cm-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-5555"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cm-notes">Notes for admin (optional)</Label>
            <Textarea
              id="cm-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional context for the SF admin"
            />
          </div>

          {!adminConfigured && (
            <div className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
              Admin email is not configured. Ask an admin to set SF_ADMIN_EMAIL before submitting.
            </div>
          )}
          {!mailboxConnected && (
            <div className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
              Connect your mailbox in Settings before submitting.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || upsert.isPending}>
            {upsert.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NeedsReviewBanner({
  count,
  suppliers,
  onResolved,
  isAdmin,
}: {
  count: number;
  suppliers: TdmSupplier[];
  onResolved: () => void;
  isAdmin: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const merge = useMergeSupplier();
  const dismiss = useDismissSupplierReview();

  const handleMerge = (sourceId: string, targetId: string) => {
    if (!window.confirm("Merge this Salesforce-imported supplier into the existing TDM entry? The Salesforce link will be transferred.")) return;
    merge.mutate(
      { id: sourceId, data: { targetId } },
      {
        onSuccess: () => { toast.success("Merged into existing supplier"); onResolved(); },
        onError: (err) => toastError(err, "Failed to merge suppliers"),
      },
    );
  };

  const handleDismiss = (id: string) => {
    dismiss.mutate(
      { id },
      {
        onSuccess: () => { toast.success("Review dismissed"); onResolved(); },
        onError: (err) => toastError(err, "Failed to dismiss review"),
      },
    );
  };

  return (
    <div className="border border-orange-300 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-900 rounded-lg">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full p-4 flex items-start gap-3 text-left hover:bg-orange-100/50 dark:hover:bg-orange-900/20 transition-colors rounded-t-lg"
      >
        <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="font-medium text-orange-800 dark:text-orange-300">
            {count} supplier{count === 1 ? "" : "s"} need{count === 1 ? "s" : ""} review
          </h4>
          <p className="text-sm text-orange-700/80 dark:text-orange-400/80 mt-1">
            These were imported from Salesforce and look similar to existing TDM suppliers, but the
            match wasn't confident enough to auto-link. {isAdmin ? "Confirm or dismiss each one below." : "An admin will review them."}
          </p>
        </div>
        <span className="text-xs text-orange-700 dark:text-orange-400 font-medium pt-1">
          {expanded ? "Hide" : "Show"}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-orange-200 dark:border-orange-900 divide-y divide-orange-200 dark:divide-orange-900">
          {suppliers.map((s) => {
            const r = s.needsReview!;
            const pct = Math.round(r.score * 100);
            return (
              <div key={s.id} className="p-4 flex flex-col sm:flex-row sm:items-start gap-3 justify-between">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">
                    {s.name}{" "}
                    <span className="text-muted-foreground font-normal">
                      (from Salesforce)
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">
                    Possible duplicate of{" "}
                    <span className="font-medium text-foreground">
                      {r.matchSupplierName ?? "an existing supplier"}
                    </span>{" "}
                    — {pct}% match
                  </div>
                  {r.signals.length > 0 && (
                    <ul className="mt-1 text-xs text-muted-foreground list-disc list-inside">
                      {r.signals.map((sig, idx) => (
                        <li key={idx}>{sig}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex flex-row sm:flex-col gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleMerge(s.id, r.matchSupplierId)}
                      disabled={merge.isPending || dismiss.isPending}
                    >
                      <GitMerge className="h-3.5 w-3.5 mr-1.5" />
                      Merge into existing
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDismiss(s.id)}
                      disabled={merge.isPending || dismiss.isPending}
                    >
                      Not a duplicate
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
